import store from '../store/store';
import { addOfflineFile, removeFile, type OfflineFileData } from '../store/BackingCodingSlice';
import type { FileDisplayData } from './BackingCodingFileUpload';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function generateUniqueId(cycleNum: number): string {
  return `backing_coding_${cycleNum}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export async function storeFileOffline(
  cycleNum: number,
  file: File,
  qualityTourId: string,
  productName?: string
): Promise<void> {
  const uniqueId = generateUniqueId(cycleNum);
  const previewUrl = URL.createObjectURL(file);
  const fileData = await fileToBase64(file);
  const offlineFileData: OfflineFileData = {
    id: uniqueId,
    cycleNum,
    productName: productName || undefined,
    fileData,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    timestamp: Date.now(),
    qualityTourId,
    previewUrl,
  };
  store.dispatch(addOfflineFile(offlineFileData));
}

export function getFileDisplayData(cycleNum: number, productName?: string): FileDisplayData[] {
  try {
    const state = store.getState();
    const allOfflineFiles = state.backingCoding?.offlineFiles || [];
    const offlineFiles = allOfflineFiles.filter((file: OfflineFileData) => {
      const fileCycleNum = typeof file.cycleNum === 'string' ? parseInt(file.cycleNum, 10) : file.cycleNum;
      const matchesCycle = fileCycleNum === cycleNum;
      const fileProduct = (file.productName || '').toString().toLowerCase();
      const matchesProduct = !productName || fileProduct === (productName || '').toLowerCase();
      return matchesCycle && matchesProduct;
    });
    return offlineFiles.map((file: OfflineFileData) => ({
      id: file.id,
      cycleNum: file.cycleNum,
      productName: file.productName,
      fileUrl: file.previewUrl || '',
      fileName: file.fileName,
      fileSize: file.fileSize,
      uploadTime: file.timestamp,
      isOffline: true,
    }));
  } catch (e) {
    return [];
  }
}

export function base64ToFile(base64: string, fileName: string, mimeType: string): File {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new File([new Uint8Array(byteNumbers)], fileName, { type: mimeType });
}

export async function uploadOfflineFile(
  offlineFile: OfflineFileData,
  accessToken: string
): Promise<{ success: boolean; message: string; data?: any }> {
  try {
    const file = base64ToFile(offlineFile.fileData, offlineFile.fileName, offlineFile.fileType);
    const { uploadFileToSharePointWithFile } = await import('./BackingCodingFileUpload');
    const result = await uploadFileToSharePointWithFile(
      offlineFile.cycleNum,
      offlineFile.qualityTourId,
      accessToken,
      file,
      offlineFile.productName
    );
    if (result.success) store.dispatch(removeFile(offlineFile.id));
    return result;
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to upload offline file',
    };
  }
}

export async function syncOfflineFiles(accessToken: string): Promise<{ success: boolean; message: string }> {
  try {
    const state = store.getState();
    const allOfflineFiles = state.backingCoding?.offlineFiles || [];
    // Only sync files with fileData (uploaded while offline), not fetched files
    const offlineFiles = allOfflineFiles.filter((f: any) => f.fileData && f.fileData.trim() !== '');
    
    if (offlineFiles.length === 0) return { success: true, message: 'No offline files to sync' };

    store.dispatch({ type: 'backingCoding/setSyncing', payload: true });
    const results: any[] = [];
    const errors: string[] = [];
    const uploadedFileIds: string[] = [];

    for (const offlineFile of offlineFiles) {
      try {
        const result = await uploadOfflineFile(offlineFile, accessToken);
        if (result.success) {
          uploadedFileIds.push(offlineFile.id);
          results.push(result);
        } else {
          errors.push(`Cycle ${offlineFile.cycleNum}: ${result.message}`);
        }
      } catch (error) {
        errors.push(`Cycle ${offlineFile.cycleNum}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    uploadedFileIds.forEach(fileId => store.dispatch(removeFile(fileId)));
    store.dispatch({ type: 'backingCoding/setSyncing', payload: false });

    if (errors.length > 0) {
      return { success: false, message: `Some files failed to sync: ${errors.join(', ')}` };
    }
    return { success: true, message: `Successfully synced ${results.length} offline files` };
  } catch (error) {
    store.dispatch({ type: 'backingCoding/setSyncing', payload: false });
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to sync offline files',
    };
  }
}
